<template>
  <div class="invoice-dense-tab">
    <app-collapse class="mb-1 invoice-dense-filters">
      <app-collapse-item title="Filters">
        <template #header>
          <span class="invoice-dense-filters__title">
            <feather-icon icon="FilterIcon" size="15" />
            <span>Filters</span>
          </span>
        </template>
        <div class="p-2">
          <b-row>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Start Date">
                <b-form-input v-model="filters.startDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="End Date">
                <b-form-input v-model="filters.endDate" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3">
              <b-form-group label="Client">
                <b-form-select v-model="filters.client" :options="clientSelectOptions" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="3" class="d-flex align-items-end">
              <b-button variant="primary" size="sm" @click="applyFilters" class="mr-2">Apply</b-button>
              <b-button variant="outline-secondary" size="sm" @click="clearFilters">Clear</b-button>
            </b-col>
          </b-row>
        </div>
      </app-collapse-item>
    </app-collapse>

    <div class="invoice-dense-toolbar">
      <div class="invoice-dense-toolbar__search">
        <b-input-group size="sm">
          <template #prepend>
            <b-input-group-text class="invoice-dense-search-icon">
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-text>
          </template>
          <b-form-input v-model="search" placeholder="Search invoice #, client…" :debounce="200" />
          <template v-if="search" #append>
            <b-button size="sm" variant="outline-secondary" @click="search = ''">
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </template>
        </b-input-group>
      </div>
      <div class="invoice-dense-toolbar__meta">
        <span v-if="!loading">{{ filteredItems.length }} collection{{ filteredItems.length === 1 ? '' : 's' }}</span>
        <span v-else>Loading…</span>
      </div>
    </div>

    <div class="invoice-dense-table-wrap">
      <b-table
        :items="filteredItems"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        hover
        responsive
        :busy="loading"
        show-empty
        class="invoice-dense-table mb-0"
      >
        <template #table-busy>
          <div class="invoice-dense-state invoice-dense-state--loading">
            <b-spinner small class="mr-50" />
            <span>Loading collections…</span>
          </div>
        </template>
        <template #empty>
          <div class="invoice-dense-state">
            <div class="invoice-dense-empty-icon">
              <feather-icon icon="InboxIcon" size="28" />
            </div>
            <h6 class="mb-25">No collections data</h6>
            <p class="text-muted mb-1">
              {{ search ? 'Try a different search or clear filters.' : 'No outstanding follow-ups for the current filters.' }}
            </p>
            <b-button v-if="search" size="sm" variant="outline-secondary" @click="search = ''">Clear search</b-button>
          </div>
        </template>
        <template #cell(invoice_info)="{ item }">
          <div class="inv-cell-stack">
            <b-link class="inv-invoice-link" @click="$emit('view-invoice', item)">{{ item.invoice_id || item.invoice_number || 'N/A' }}</b-link>
            <small class="text-muted">{{ formatDate(item.invoice_date) }}</small>
          </div>
        </template>
        <template #cell(client_gstin)="{ item }">
          <div class="inv-cell-stack">
            <span class="inv-primary-text">{{ item.client_name || 'N/A' }}</span>
            <small v-if="item.client_gst" class="inv-secondary-text">{{ item.client_gst }}</small>
          </div>
        </template>
        <template #cell(days_overdue)="{ item }">
          <b-badge :variant="getOverdueVariant(item.days_overdue)" class="inv-status-badge">{{ item.days_overdue }} days</b-badge>
        </template>
        <template #cell(follow_up_status)="{ item }">
          <b-badge :variant="getFollowUpVariant(item.follow_up_status)" class="inv-status-badge">{{ item.follow_up_status }}</b-badge>
        </template>
        <template #cell(last_follow_up_date)="{ item }">
          <span>{{ item.last_follow_up_date ? formatDate(item.last_follow_up_date) : '—' }}</span>
        </template>
        <template #cell(outstanding_amount)="{ item }">
          <span class="inv-amount text-danger">₹{{ formatIndianNumber(item.outstanding_amount || 0) }}</span>
        </template>
        <template #cell(actions)="{ item }">
          <div class="inv-row-actions">
            <b-button size="sm" variant="flat-primary" class="btn-icon" aria-label="Add Follow-up" @click="$emit('add-followup', item)">
              <feather-icon icon="MessageSquareIcon" size="14" />
            </b-button>
            <b-button size="sm" variant="flat-success" class="btn-icon" aria-label="Record Payment" @click="$emit('record-payment', item)">
              <feather-icon icon="DollarSignIcon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>

    <div v-if="filteredItems.length > perPage" class="invoice-dense-pagination">
      <b-pagination v-model="currentPage" :total-rows="filteredItems.length" :per-page="perPage" align="right" size="sm" class="mb-0" />
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BBadge, BButton, BLink, BSpinner, BPagination } from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import moment from 'moment'
import invoiceFormatMixin from '@/mixins/invoiceFormatMixin'

function thisMonthRange() {
  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  }
}

export default {
  name: 'CollectionsTab',
  components: { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupText, BTable, BBadge, BButton, BLink, BSpinner, BPagination, AppCollapse, AppCollapseItem },
  mixins: [invoiceFormatMixin],
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    loading: Boolean,
    perPage: { type: Number, default: 20 },
    clients: { type: Array, default: () => [] },
    initialFilters: {
      type: Object,
      default: () => ({ startDate: null, endDate: null, client: null })
    }
  },
  data() {
    return {
      search: '', currentPage: 1,
      filters: {
        startDate: this.initialFilters.startDate || null,
        endDate: this.initialFilters.endDate || null,
        client: this.initialFilters.client || null
      }
    }
  },
  computed: {
    clientSelectOptions() {
      return [
        { value: null, text: 'Select Client' },
        ...(this.clients || []).map(c => ({
          value: c.title != null ? c.title : (c.text != null ? c.text : c.value),
          text: c.text || c.title || String(c.value ?? '')
        }))
      ]
    },
    filteredItems() {
      let data = [...this.items]
      if (this.filters.client) data = data.filter(i => i.client_name === this.filters.client)
      if (this.search) {
        const q = this.search.toLowerCase()
        data = data.filter(i => (i.invoice_id || '').toLowerCase().includes(q) || (i.client_name || '').toLowerCase().includes(q))
      }
      return data
    }
  },
  watch: {
    search() { this.currentPage = 1 },
    items() { this.currentPage = 1 }
  },
  methods: {
    applyFilters() { this.$emit('apply-filters', { ...this.filters }) },
    clearFilters() {
      const range = thisMonthRange()
      this.filters = { startDate: range.startDate, endDate: range.endDate, client: null }
      this.$emit('apply-filters', { ...this.filters })
    }
  }
}
</script>

<style lang="scss">
@import './invoice-dense-tab.scss';
</style>
